import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Eye, EyeOff, Mail, Star } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import BlogEditor from "./BlogEditor";

import EmailLogManager from "./EmailLogManager";
import SiteCMSTab from "./SiteCMSTab";
import BlogAnalyticsPanel from "./BlogAnalyticsPanel";
const SUBTABS = [
  { key: "site",          label: "Site Content" },
  { key: "blog",          label: "Blog posts" },
  { key: "blog-analytics", label: "Blog analytics" },
  { key: "testimonials",  label: "Testimonials" },
  { key: "waitlist",      label: "Waitlist" },
  { key: "emails",        label: "Email log" },
];

export default function ContentTab() {
  const [sub, setSub] = useState("site");
  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-gg-bg border border-gg-border rounded-full p-1 w-fit flex-wrap">
        {SUBTABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setSub(t.key)}
            data-testid={`content-subtab-${t.key}`}
            className={`text-xs px-4 py-1.5 rounded-full transition-colors ${
              sub === t.key ? "bg-gg-green text-white" : "text-gg-brown hover:text-[var(--gg-text)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === "site" && <SiteCMSTab />}
      {sub === "blog" && <BlogManager />}
      {sub === "blog-analytics" && <BlogAnalyticsPanel />}
      {sub === "testimonials" && <TestimonialManager />}
      {sub === "waitlist" && <WaitlistManager />}
      {sub === "emails" && <EmailLogManager />}
    </div>
  );
}

// =================================================================
// Blog
// =================================================================
function BlogManager() {
  const { data: posts = [], refetch } = useQuery({
    queryKey: ["adminBlog"],
    queryFn: async () => (await api.get("/admin/blog")).data,
  });
  const [editing, setEditing] = useState(null);

  const del = async (p) => {
    if (!confirm(`Delete blog post "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Post deleted");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={() => setEditing({ title: "", slug: "", excerpt: "", cover_image_url: "", content_html: "", published: false, tags: [] })}
          className="btn-pill btn-primary"
          data-testid="blog-new-btn"
        >
          <Plus size={14} /> New post
        </button>
      </div>
      {posts.length === 0 ? (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">
          No blog posts yet. Click "New post" to write your first.
        </div>
      ) : (
        <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Updated</th>
                <th className="px-4 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-gg-border" data-testid={`blog-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{p.title}</div>
                    <div className="text-xs text-gg-muted">/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    {p.published ? (
                      <span className="inline-flex items-center gap-1 text-xs text-gg-green"><Eye size={12} /> Published</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-gg-muted"><EyeOff size={12} /> Draft</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gg-muted">{formatDate(p.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => setEditing(p)} className="text-xs px-3 py-1 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green mr-1">
                      <Edit2 size={12} /> Edit
                    </button>
                    <button type="button" onClick={() => del(p)} className="text-xs px-2 py-1 rounded-full text-gg-muted hover:text-[#B24C4C]">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <BlogEditDialog post={editing} allPosts={posts} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />
      )}
    </div>
  );
}

function BlogEditDialog({ post, allPosts = [], onClose, onSaved }) {
  const isEdit = !!post.id;
  const [form, setForm] = useState({
    ...post,
    meta_title: post.meta_title || "",
    meta_description: post.meta_description || "",
    focus_keyword: post.focus_keyword || "",
    cover_image_alt: post.cover_image_alt || "",
    canonical_url: post.canonical_url || "",
    key_takeaways: post.key_takeaways || [],
    faq: post.faq || [],
    categories: post.categories || [],
    tags: post.tags || [],
    is_hub: !!post.is_hub,
    hub_post_id: post.hub_post_id || "",
    cta: post.cta || { type: "none", target_id: "", url: "", label: "" },
    recipe: post.recipe || null,
  });
  const [busy, setBusy] = useState(false);
  const [busyAi, setBusyAi] = useState(null); // "draft" | "faq" | "takeaways" | null
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  // ─── SEO / AI content-quality warnings ───
  const warnings = useMemo(() => {
    const w = [];
    const excerptLen = (form.excerpt || "").trim().length;
    const html = form.content_html || "";
    const wordCount = html.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
    const h2Count = (html.match(/<h2\b/gi) || []).length;
    const h3Count = (html.match(/<h3\b/gi) || []).length;
    const imgCount = (html.match(/<img\b/gi) || []).length;
    const imgWithoutAlt = (html.match(/<img\b(?![^>]*\balt=)[^>]*>/gi) || []).length;
    const metaDescLen = (form.meta_description || form.excerpt || "").trim().length;
    const metaTitleLen = (form.meta_title || form.title || "").trim().length;

    if (excerptLen < 80) w.push(`Excerpt is only ${excerptLen} chars — aim for 140–160 for search snippets.`);
    if (excerptLen > 200) w.push(`Excerpt is ${excerptLen} chars — Google truncates around 160.`);
    if (metaTitleLen > 60) w.push(`Meta title is ${metaTitleLen} chars — Google truncates around 60.`);
    if (metaDescLen < 120) w.push(`Meta description is ${metaDescLen} chars — sweet spot is 140–160.`);
    if (metaDescLen > 165) w.push(`Meta description is ${metaDescLen} chars — Google truncates around 160.`);
    if (wordCount < 300) w.push(`Only ${wordCount} words — Google + AI Overviews favor 800+ for topic authority.`);
    if (h2Count === 0) w.push(`No H2 headings — headings help AI crawlers extract topical structure.`);
    if (h2Count + h3Count > 12) w.push(`${h2Count + h3Count} headings — consider consolidating to 4–8 for readability.`);
    if (imgCount === 0) w.push(`No images — one relevant image improves engagement + shareability.`);
    if (imgWithoutAlt > 0) w.push(`${imgWithoutAlt} image(s) missing alt text — required for SEO + accessibility.`);
    if (!(form.cover_image_url || "").trim()) w.push(`No cover image — shared links unfurl with the default site image.`);
    if (form.cover_image_url && !(form.cover_image_alt || "").trim()) w.push(`Cover image has no alt text.`);
    if ((form.key_takeaways || []).length === 0) w.push(`No key-takeaways — AI Overviews often quote these verbatim.`);
    if ((form.faq || []).length === 0) w.push(`No FAQ — a 3–5 question FAQ dramatically boosts AI Overview appearances.`);
    if (!(form.focus_keyword || "").trim()) w.push(`No focus keyword — helps you stay on-topic while writing.`);
    else {
      const kw = form.focus_keyword.trim().toLowerCase();
      const inTitle = (form.title || "").toLowerCase().includes(kw);
      const inFirst100 = _stripHtml(html).slice(0, 100).toLowerCase().includes(kw);
      if (!inTitle) w.push(`Focus keyword "${form.focus_keyword}" not in the title.`);
      if (!inFirst100) w.push(`Focus keyword "${form.focus_keyword}" not in the first 100 words.`);
    }
    return { list: w, wordCount, h2Count, imgCount, imgWithoutAlt };
  }, [form]);

  const save = async () => {
    if (!form.title.trim() || !form.content_html.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setBusy(true);
    try {
      // Strip empty CTA / recipe so backend stores null instead of empty stub
      const cta = form.cta && form.cta.type && form.cta.type !== "none" ? form.cta : null;
      const recipe = form.recipe && (
        form.recipe.recipe_yield || (form.recipe.ingredients || []).length ||
        (form.recipe.instructions || []).length
      ) ? form.recipe : null;
      const payload = {
        ...form,
        meta_title: form.meta_title || null,
        meta_description: form.meta_description || null,
        hub_post_id: form.hub_post_id || null,
        cta, recipe,
      };
      if (isEdit) {
        await api.patch(`/admin/blog/${post.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Post created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-gg-surface max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{isEdit ? "Edit post" : "New blog post"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input value={form.title} onChange={set("title")} placeholder="Title" className="text-lg font-semibold" data-testid="blog-title" />
          <Input value={form.slug} onChange={set("slug")} placeholder="URL slug (auto-generated from title if blank)" />

          {/* AI helpers */}
          <div className="flex flex-wrap gap-2 items-center rounded-xl border border-gg-green/30 bg-gg-green/5 p-3">
            <span className="text-xs uppercase tracking-[0.14em] text-gg-green font-semibold mr-2">✨ AI helpers</span>
            <button
              type="button"
              disabled={busyAi === "draft"}
              onClick={async () => {
                if (!form.title.trim()) { toast.error("Add a title first"); return; }
                const hasContent = (form.content_html || "").replace(/<[^>]+>/g, "").trim().length > 20;
                if (hasContent && !window.confirm("Replace the current draft with a fresh AI-generated one? Your existing content will be lost.")) return;
                setBusyAi("draft");
                try {
                  const { data } = await api.post("/admin/blog/ai/draft", {
                    title: form.title, focus_keyword: form.focus_keyword || "", target_word_count: 900,
                  });
                  setForm((f) => ({
                    ...f,
                    excerpt: data.excerpt || f.excerpt,
                    meta_description: data.meta_description || f.meta_description,
                    content_html: data.content_html || f.content_html,
                    key_takeaways: data.key_takeaways || f.key_takeaways,
                    faq: data.faq || f.faq,
                  }));
                  toast.success("AI draft loaded — edit + save when ready");
                } catch (e) { toast.error(e?.response?.data?.detail || "Draft failed"); }
                finally { setBusyAi(null); }
              }}
              className="text-xs px-3 py-1.5 rounded-full bg-gg-green text-white hover:bg-gg-green/90 disabled:opacity-50"
              data-testid="blog-ai-draft-btn"
            >
              {busyAi === "draft" ? "Drafting…" : "✍️ Draft with AI"}
            </button>
            <button
              type="button"
              disabled={busyAi === "faq"}
              onClick={async () => {
                if (!(form.content_html || "").trim()) { toast.error("Write some content first"); return; }
                setBusyAi("faq");
                try {
                  const { data } = await api.post("/admin/blog/ai/suggest-faq", {
                    title: form.title || "Post", content_html: form.content_html,
                    focus_keyword: form.focus_keyword || "",
                  });
                  setForm((f) => ({ ...f, faq: data.faq || [] }));
                  toast.success(`Suggested ${data.faq?.length || 0} FAQ items`);
                } catch (e) { toast.error(e?.response?.data?.detail || "FAQ suggest failed"); }
                finally { setBusyAi(null); }
              }}
              className="text-xs px-3 py-1.5 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green disabled:opacity-50"
              data-testid="blog-ai-faq-btn"
            >
              {busyAi === "faq" ? "…" : "❓ Suggest FAQ"}
            </button>
            <button
              type="button"
              disabled={busyAi === "takeaways"}
              onClick={async () => {
                if (!(form.content_html || "").trim()) { toast.error("Write some content first"); return; }
                setBusyAi("takeaways");
                try {
                  const { data } = await api.post("/admin/blog/ai/suggest-takeaways", {
                    title: form.title || "Post", content_html: form.content_html,
                  });
                  setForm((f) => ({ ...f, key_takeaways: data.key_takeaways || [] }));
                  toast.success(`Suggested ${data.key_takeaways?.length || 0} takeaways`);
                } catch (e) { toast.error(e?.response?.data?.detail || "Takeaway suggest failed"); }
                finally { setBusyAi(null); }
              }}
              className="text-xs px-3 py-1.5 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green disabled:opacity-50"
              data-testid="blog-ai-takeaways-btn"
            >
              {busyAi === "takeaways" ? "…" : "✦ Suggest takeaways"}
            </button>
          </div>

          <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
            <Input value={form.cover_image_url} onChange={set("cover_image_url")} placeholder="Cover image URL" />
            <Input value={form.cover_image_alt} onChange={set("cover_image_alt")} placeholder="Cover image alt text (SEO)" data-testid="blog-cover-alt" />
          </div>
          <Textarea value={form.excerpt} onChange={set("excerpt")} rows={2} placeholder="Excerpt (1–2 sentences for the blog index)" />

          {/* SEO section */}
          <details className="rounded-xl border border-gg-border bg-gg-bg p-4" open>
            <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
              SEO &amp; AI metadata
            </summary>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs text-gg-muted">Meta title (browser tab + search result title) — leave blank to reuse post title</label>
                <Input value={form.meta_title} onChange={set("meta_title")} placeholder="e.g. How to Start a Homestead Garden in Arizona" data-testid="blog-meta-title" />
              </div>
              <div>
                <label className="text-xs text-gg-muted">Meta description (search snippet, 140–160 chars) — leave blank to reuse excerpt</label>
                <Textarea value={form.meta_description} onChange={set("meta_description")} rows={2} data-testid="blog-meta-desc" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gg-muted">Focus keyword (the phrase you want to rank for)</label>
                  <Input value={form.focus_keyword} onChange={set("focus_keyword")} placeholder="e.g. homestead classes arizona" data-testid="blog-focus-keyword" />
                </div>
                <div>
                  <label className="text-xs text-gg-muted">Canonical URL (leave blank unless post is a duplicate)</label>
                  <Input value={form.canonical_url} onChange={set("canonical_url")} placeholder="https://…" />
                </div>
              </div>
            </div>
          </details>

          {/* Key takeaways */}
          <details className="rounded-xl border border-gg-border bg-gg-bg p-4">
            <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
              Key takeaways ({(form.key_takeaways || []).length}) — LLMs quote these directly
            </summary>
            <RepeatableList
              items={form.key_takeaways || []}
              onChange={(next) => setForm((f) => ({ ...f, key_takeaways: next }))}
              placeholder="e.g. Arizona high desert works best with drip irrigation + morning shade"
              addLabel="Add takeaway"
              testId="blog-takeaway"
            />
          </details>

          {/* FAQ */}
          <details className="rounded-xl border border-gg-border bg-gg-bg p-4">
            <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
              FAQ ({(form.faq || []).length}) — big AI Overview signal, aim for 3–5 questions
            </summary>
            <FaqEditor
              items={form.faq || []}
              onChange={(next) => setForm((f) => ({ ...f, faq: next }))}
            />
          </details>

          <BlogTaxonomyEditor form={form} setForm={setForm} allPosts={allPosts} />
          <BlogCtaEditor form={form} setForm={setForm} />
          <BlogRecipeEditor form={form} setForm={setForm} />

          <BlogEditor value={form.content_html} onChange={(html) => setForm((f) => ({ ...f, content_html: html }))} />

          {/* Live warnings */}
          <SeoWarnings warnings={warnings} />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              data-testid="blog-published-toggle"
            />
            <span>Published (visible on /blog)</span>
          </label>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="blog-save">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create post"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function _stripHtml(html) {
  return String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

const BLOG_CATEGORIES = [
  "Recipe", "Garden", "Livestock", "How-to", "ESA-lesson", "Homestead life", "Seasonal",
];

function BlogTaxonomyEditor({ form, setForm, allPosts }) {
  const [tagDraft, setTagDraft] = useState("");
  const toggleCategory = (c) => {
    const cur = form.categories || [];
    setForm((f) => ({
      ...f,
      categories: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c],
    }));
  };
  const addTag = () => {
    const v = tagDraft.trim().toLowerCase();
    if (!v) return;
    if ((form.tags || []).includes(v)) { setTagDraft(""); return; }
    setForm((f) => ({ ...f, tags: [...(f.tags || []), v] }));
    setTagDraft("");
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: (f.tags || []).filter((x) => x !== t) }));
  const hubCandidates = (allPosts || []).filter((p) => p.is_hub && p.id !== form.id);

  return (
    <details className="rounded-xl border border-gg-border bg-gg-bg p-4">
      <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
        Categories, tags &amp; hub-spoke
      </summary>
      <div className="mt-4 space-y-4">
        <div>
          <label className="text-xs text-gg-muted block mb-1.5">Categories (pick one or more)</label>
          <div className="flex flex-wrap gap-2">
            {BLOG_CATEGORIES.map((c) => {
              const on = (form.categories || []).includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCategory(c)}
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${on ? "bg-gg-green text-white" : "bg-gg-surface border border-gg-border text-gg-brown"}`}
                  data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-xs text-gg-muted block mb-1.5">Tags (specific keywords — used in `/blog?tag=…`)</label>
          <div className="flex gap-2 items-center">
            <Input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="e.g. sourdough, drip irrigation, chickens"
              data-testid="blog-tag-input"
            />
            <button type="button" onClick={addTag} className="btn-pill btn-outline text-xs px-3 shrink-0">+ Add tag</button>
          </div>
          {(form.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-gg-brown/10 text-gg-brown">
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-[#B24C4C]" aria-label={`Remove ${t}`}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-gg-border pt-3">
          <label className="flex items-center gap-2 text-sm mb-2">
            <input
              type="checkbox"
              checked={!!form.is_hub}
              onChange={(e) => setForm((f) => ({ ...f, is_hub: e.target.checked, hub_post_id: e.target.checked ? "" : f.hub_post_id }))}
              data-testid="blog-is-hub-toggle"
            />
            <span><strong>This is a hub post</strong> — a pillar page that spokes link back to</span>
          </label>
          {!form.is_hub && (
            <div>
              <label className="text-xs text-gg-muted block mb-1.5">Belongs to hub (leave blank if this is a standalone post)</label>
              <select
                value={form.hub_post_id || ""}
                onChange={(e) => setForm((f) => ({ ...f, hub_post_id: e.target.value }))}
                className="w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
                data-testid="blog-hub-select"
              >
                <option value="">— None (standalone) —</option>
                {hubCandidates.map((h) => (
                  <option key={h.id} value={h.id}>{h.title}</option>
                ))}
              </select>
              {hubCandidates.length === 0 && (
                <p className="text-[11px] text-gg-muted mt-1 italic">
                  No hub posts yet. Mark another post as &quot;hub&quot; first, then link spokes to it.
                </p>
              )}
            </div>
          )}
          <p className="text-[11px] text-gg-muted mt-2 italic">
            Hub-spoke pattern: hub post = pillar guide, spokes = subtopic deep-dives. Hub shows spokes below the content; spokes show &quot;Part of: [hub]&quot; at the top. Great for SEO.
          </p>
        </div>
      </div>
    </details>
  );
}

function BlogCtaEditor({ form, setForm }) {
  const { data: classes = [] } = useQuery({
    queryKey: ["adminClassesForCta"],
    queryFn: async () => (await api.get("/admin/classes")).data,
  });
  const cta = form.cta || { type: "none", target_id: "", url: "", label: "" };
  const setCta = (patch) => setForm((f) => ({ ...f, cta: { ...cta, ...patch } }));
  const placeholderLabel = {
    class: "Register — from $55",
    consultation: "Request a Consultation",
    "group-class": "Book a Private Class",
    waitlist: "Join the Waitlist",
    external: "Learn more",
  }[cta.type] || "";
  return (
    <details className="rounded-xl border border-gg-border bg-gg-bg p-4">
      <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
        Call-to-Action button {cta.type !== "none" && <span className="text-gg-green">· enabled</span>}
      </summary>
      <div className="mt-4 space-y-3">
        <div>
          <label className="text-xs text-gg-muted block mb-1.5">What should this post drive?</label>
          <select
            value={cta.type}
            onChange={(e) => setCta({ type: e.target.value, target_id: "", url: "" })}
            className="w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
            data-testid="blog-cta-type"
          >
            <option value="none">None (no CTA button)</option>
            <option value="class">Register for a specific class</option>
            <option value="consultation">Request a property consultation</option>
            <option value="group-class">Request a private group class</option>
            <option value="waitlist">Join a waitlist</option>
            <option value="external">External URL</option>
          </select>
        </div>
        {cta.type === "class" && (
          <div>
            <label className="text-xs text-gg-muted block mb-1.5">Class</label>
            <select
              value={cta.target_id || ""}
              onChange={(e) => setCta({ target_id: e.target.value })}
              className="w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
              data-testid="blog-cta-class"
            >
              <option value="">— Pick a class —</option>
              {classes.filter((c) => c.status === "open").map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title} — {c.class_date || "(no date)"}
                </option>
              ))}
            </select>
          </div>
        )}
        {cta.type === "external" && (
          <div>
            <label className="text-xs text-gg-muted block mb-1.5">External URL</label>
            <Input
              value={cta.url || ""}
              onChange={(e) => setCta({ url: e.target.value })}
              placeholder="https://…"
              data-testid="blog-cta-url"
            />
          </div>
        )}
        {cta.type !== "none" && (
          <div>
            <label className="text-xs text-gg-muted block mb-1.5">Button label (optional — sensible default used if blank)</label>
            <Input
              value={cta.label || ""}
              onChange={(e) => setCta({ label: e.target.value })}
              placeholder={placeholderLabel}
              data-testid="blog-cta-label"
            />
          </div>
        )}
        <p className="text-[11px] text-gg-muted italic">
          The CTA button appears at the end of the post body. Clicks are counted in the Analytics tab, so you can measure which posts drive real bookings.
        </p>
      </div>
    </details>
  );
}

function BlogRecipeEditor({ form, setForm }) {
  const recipe = form.recipe || null;
  const enabled = !!recipe;
  const setField = (k, v) => setForm((f) => ({ ...f, recipe: { ...(f.recipe || {}), [k]: v } }));
  const setList = (k, v) => setField(k, v);
  const enable = () => setForm((f) => ({
    ...f,
    recipe: {
      recipe_yield: "", prep_time_min: null, cook_time_min: null,
      total_time_min: null, course: "", cuisine: "",
      ingredients: [""], instructions: [""], notes: "",
    },
  }));
  const disable = () => setForm((f) => ({ ...f, recipe: null }));
  return (
    <details className="rounded-xl border border-gg-border bg-gg-bg p-4">
      <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">
        Recipe block {enabled && <span className="text-gg-green">· enabled (emits Recipe schema)</span>}
      </summary>
      <div className="mt-4 space-y-3">
        {!enabled ? (
          <button type="button" onClick={enable} className="btn-pill btn-outline text-xs" data-testid="blog-recipe-enable">
            + Add recipe block
          </button>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gg-muted block mb-1">Yield</label>
                <Input value={recipe.recipe_yield || ""} onChange={(e) => setField("recipe_yield", e.target.value)} placeholder="4 servings · 1 loaf" data-testid="blog-recipe-yield" />
              </div>
              <div>
                <label className="text-xs text-gg-muted block mb-1">Course</label>
                <Input value={recipe.course || ""} onChange={(e) => setField("course", e.target.value)} placeholder="breakfast · dinner · dessert · side" />
              </div>
              <div>
                <label className="text-xs text-gg-muted block mb-1">Prep time (min)</label>
                <Input type="number" min="0" value={recipe.prep_time_min ?? ""} onChange={(e) => setField("prep_time_min", e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div>
                <label className="text-xs text-gg-muted block mb-1">Cook time (min)</label>
                <Input type="number" min="0" value={recipe.cook_time_min ?? ""} onChange={(e) => setField("cook_time_min", e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div>
                <label className="text-xs text-gg-muted block mb-1">Total time (min)</label>
                <Input type="number" min="0" value={recipe.total_time_min ?? ""} onChange={(e) => setField("total_time_min", e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div>
                <label className="text-xs text-gg-muted block mb-1">Cuisine</label>
                <Input value={recipe.cuisine || ""} onChange={(e) => setField("cuisine", e.target.value)} placeholder="American, Mexican, Mediterranean…" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gg-muted block mb-1.5">Ingredients (one per line)</label>
              <Textarea
                rows={5}
                value={(recipe.ingredients || []).join("\n")}
                onChange={(e) => setList("ingredients", e.target.value.split("\n"))}
                placeholder={"2 cups whole wheat flour\n1 tsp sea salt\n1 cup warm water"}
                data-testid="blog-recipe-ingredients"
              />
            </div>
            <div>
              <label className="text-xs text-gg-muted block mb-1.5">Instructions (one step per line)</label>
              <Textarea
                rows={6}
                value={(recipe.instructions || []).join("\n")}
                onChange={(e) => setList("instructions", e.target.value.split("\n"))}
                placeholder={"Combine dry ingredients in a large bowl.\nAdd warm water and mix until shaggy.\nRest for 30 minutes…"}
                data-testid="blog-recipe-instructions"
              />
            </div>
            <div>
              <label className="text-xs text-gg-muted block mb-1.5">Recipe notes (optional)</label>
              <Textarea rows={2} value={recipe.notes || ""} onChange={(e) => setField("notes", e.target.value)} placeholder="Storage, variations, tips…" />
            </div>
            <button type="button" onClick={disable} className="text-xs text-[#B24C4C] hover:underline" data-testid="blog-recipe-disable">
              Remove recipe block
            </button>
          </>
        )}
      </div>
    </details>
  );
}

function RepeatableList({ items, onChange, placeholder, addLabel, testId }) {
  const set = (i, val) => onChange(items.map((it, idx) => (idx === i ? val : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div className="mt-3 space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-start">
          <Textarea
            rows={2}
            value={it}
            onChange={(e) => set(i, e.target.value)}
            placeholder={placeholder}
            data-testid={`${testId}-${i}`}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-xs text-gg-muted hover:text-[#B24C4C] mt-2"
            aria-label="Remove"
          >
            ✕
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs px-3 py-1 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green" data-testid={`${testId}-add`}>
        + {addLabel}
      </button>
    </div>
  );
}

function FaqEditor({ items, onChange }) {
  const set = (i, key, val) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { question: "", answer: "" }]);
  return (
    <div className="mt-3 space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-gg-border bg-gg-surface p-3 space-y-2">
          <div className="flex items-start gap-2">
            <Input
              value={it.question || ""}
              onChange={(e) => set(i, "question", e.target.value)}
              placeholder="Question (e.g. Does ESA cover homestead classes?)"
              data-testid={`blog-faq-q-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-xs text-gg-muted hover:text-[#B24C4C]"
              aria-label="Remove"
            >
              ✕
            </button>
          </div>
          <Textarea
            rows={3}
            value={it.answer || ""}
            onChange={(e) => set(i, "answer", e.target.value)}
            placeholder="Answer — plain text or HTML"
            data-testid={`blog-faq-a-${i}`}
          />
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs px-3 py-1 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green" data-testid="blog-faq-add">
        + Add FAQ question
      </button>
    </div>
  );
}

function SeoWarnings({ warnings }) {
  if (!warnings) return null;
  const { list, wordCount, h2Count, imgCount } = warnings;
  return (
    <div className="rounded-xl border border-gg-border bg-gg-bg p-4" data-testid="blog-seo-warnings">
      <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-gg-muted">
        <span><strong className="text-[var(--gg-text)]">{wordCount}</strong> words</span>
        <span><strong className="text-[var(--gg-text)]">{h2Count}</strong> H2 headings</span>
        <span><strong className="text-[var(--gg-text)]">{imgCount}</strong> images</span>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-gg-green font-semibold">✓ Looking good — no SEO warnings.</p>
      ) : (
        <>
          <p className="text-xs uppercase tracking-[0.18em] text-[#9b6a26] font-semibold mb-2">
            {list.length} SEO {list.length === 1 ? "suggestion" : "suggestions"}
          </p>
          <ul className="space-y-1 text-sm text-[var(--gg-text)]">
            {list.map((w, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-[#D9933B] mt-0.5" aria-hidden="true">⚠</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// =================================================================
// Testimonials
// =================================================================
function TestimonialManager() {
  const { data: items = [], refetch } = useQuery({
    queryKey: ["adminTestimonials"],
    queryFn: async () => (await api.get("/admin/testimonials")).data,
  });
  const [editing, setEditing] = useState(null);

  const del = async (t) => {
    if (!confirm(`Delete testimonial from ${t.author_name}?`)) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Testimonial deleted");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleVisible = async (t) => {
    try {
      await api.patch(`/admin/testimonials/${t.id}`, { visible: !t.visible });
      refetch();
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={() => setEditing({ author_name: "", role: "", quote: "", rating: 5, photo_url: "", visible: true, sort_order: 0 })}
          className="btn-pill btn-primary"
          data-testid="testimonial-new-btn"
        >
          <Plus size={14} /> New testimonial
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">
          No testimonials yet. The public testimonials section will stay hidden until you add one.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="rounded-2xl border border-gg-border bg-gg-surface p-4" data-testid={`testimonial-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{t.author_name}</div>
                  <div className="text-xs text-gg-muted">{t.role}</div>
                </div>
                <div className="flex gap-1">
                  <button type="button" onClick={() => toggleVisible(t)} className={`text-xs px-2 py-1 rounded-full border ${t.visible ? "border-gg-green text-gg-green" : "border-gg-border text-gg-muted"}`}>
                    {t.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                  </button>
                  <button type="button" onClick={() => setEditing(t)} className="text-xs px-2 py-1 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green"><Edit2 size={12} /></button>
                  <button type="button" onClick={() => del(t)} className="text-xs px-2 py-1 rounded-full text-gg-muted hover:text-[#B24C4C]"><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="mt-2 flex">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={12} className={n <= t.rating ? "fill-[#D9933B] text-[#D9933B]" : "text-gg-border"} />
                ))}
              </div>
              <p className="mt-2 text-sm italic text-gg-muted">"{t.quote}"</p>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <TestimonialEditDialog testimonial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />
      )}
    </div>
  );
}

function TestimonialEditDialog({ testimonial, onClose, onSaved }) {
  const isEdit = !!testimonial.id;
  const [form, setForm] = useState(testimonial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.author_name.trim() || !form.quote.trim()) {
      toast.error("Author name and quote are required");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/testimonials/${testimonial.id}`, form);
      else        await api.post("/admin/testimonials", form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{isEdit ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={form.author_name} onChange={set("author_name")} placeholder="Author name" data-testid="testimonial-author" />
          <Input value={form.role} onChange={set("role")} placeholder="Role (e.g. ESA Parent, Homeschool Mom)" />
          <Textarea value={form.quote} onChange={set("quote")} rows={4} placeholder="The quote…" data-testid="testimonial-quote" />
          <Input value={form.photo_url} onChange={set("photo_url")} placeholder="Photo URL (optional)" />
          <div className="flex items-center gap-3 text-sm">
            <span className="text-gg-brown font-semibold">Rating:</span>
            <select value={form.rating} onChange={set("rating")} className="rounded border border-gg-border bg-gg-surface px-2 py-1">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n === 1 ? "" : "s"}</option>)}
            </select>
            <label className="flex items-center gap-2 ml-auto">
              <input type="checkbox" checked={form.visible} onChange={(e) => setForm((f) => ({ ...f, visible: e.target.checked }))} />
              <span>Visible on site</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="testimonial-save">
            {busy ? "Saving…" : isEdit ? "Save" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================
// Waitlist
// =================================================================
function WaitlistManager() {
  const { data: items = [], refetch } = useQuery({
    queryKey: ["adminWaitlist"],
    queryFn: async () => (await api.get("/admin/waitlist")).data,
  });
  const notify = async (entry) => {
    if (!confirm(`Email ${entry.parent_name} that a spot opened in "${entry.class_title}"?`)) return;
    try {
      await api.post(`/admin/waitlist/${entry.id}/notify`, {});
      toast.success(`Notified ${entry.parent_name}`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Notify failed"); }
  };
  const remove = async (entry) => {
    if (!confirm(`Remove ${entry.parent_name} from the waitlist?`)) return;
    try {
      await api.delete(`/admin/waitlist/${entry.id}`);
      refetch();
    } catch (e) { toast.error("Delete failed"); }
  };

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">
        No waitlist signups yet. When a class fills up, parents who join the waitlist will appear here.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
          <tr><th className="px-4 py-2 text-left">Parent</th><th className="px-4 py-2 text-left">Class</th><th className="px-4 py-2 text-left">Students</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-right"></th></tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} className="border-t border-gg-border" data-testid={`waitlist-row-${e.id}`}>
              <td className="px-4 py-3">
                <div className="font-semibold">{e.parent_name}</div>
                <div className="text-xs text-gg-muted">{e.parent_email}</div>
              </td>
              <td className="px-4 py-3 text-xs">{e.class_title}</td>
              <td className="px-4 py-3 text-xs">{e.student_count}</td>
              <td className="px-4 py-3 text-xs">
                {e.status === "notified" ? (
                  <span className="text-gg-green">✓ Notified {e.notified_at ? formatDate(e.notified_at) : ""}</span>
                ) : (
                  <span className="text-[#9b6a26]">Waiting</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {e.status === "waiting" && (
                  <button type="button" onClick={() => notify(e)} className="text-xs px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white inline-flex items-center gap-1 mr-1" data-testid={`waitlist-notify-${e.id}`}>
                    <Mail size={12} /> Notify
                  </button>
                )}
                <button type="button" onClick={() => remove(e)} className="text-xs px-2 py-1 rounded-full text-gg-muted hover:text-[#B24C4C]"><Trash2 size={12} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
